import React, { useEffect, useState, useRef } from 'react';
import '../../../../../Styles/MCQAssessments/MCQAssessment.css';
import axiosInstance from '../../../../../interceptor/axiosInstance';
import { useNavigate } from 'react-router-dom';
import AssessmentEditbrn from './AssessmentEditbrn';
import CloneButton from './CloneButton';
import GenerateLinkButton from './GenerateLinkButton';

// Skeleton Loader Component
const SkeletonRow = () => (
  <div className="skeleton-row-container">
    <div className="skeleton-loader skeleton-cell-large"></div>
    <div className="skeleton-loader skeleton-cell-medium"></div>
    <div className="skeleton-loader skeleton-cell-medium"></div>
    <div className="skeleton-loader skeleton-cell-small"></div>
    <div className="skeleton-loader skeleton-cell-actions"></div>
  </div>
);

// Empty State Component
const EmptyState = ({ searchQuery, onClearSearch }) => {
  const hasSearch = searchQuery && searchQuery.trim().length > 0;
  
  return (
    <div className="empty-state">
      <div className="empty-state-icon">
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
          {hasSearch ? (
            <>
              <circle cx="32" cy="32" r="20" stroke="#cbd5e0" strokeWidth="3" fill="none"/>
              <line x1="46" y1="46" x2="60" y2="60" stroke="#cbd5e0" strokeWidth="3" strokeLinecap="round"/>
              <line x1="20" y1="32" x2="44" y2="32" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/>
            </>
          ) : (
            <>
              <rect x="15" y="10" width="50" height="60" rx="4" stroke="#cbd5e0" strokeWidth="2" fill="#f1f5f9"/>
              <line x1="25" y1="25" x2="55" y2="25" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>
              <line x1="25" y1="35" x2="55" y2="35" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>
              <line x1="25" y1="45" x2="45" y2="45" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>
            </>
          )}
        </svg>
      </div>
      <h3 className="empty-state-title">
        {hasSearch ? 'No Matching Assessments' : 'No Assessments Found'}
      </h3>
      <p className="empty-state-message">
        {hasSearch ? (
          <>
            No assessments found for <strong>"{searchQuery}"</strong>
            <br />
            Try adjusting your search terms or filters.
          </>
        ) : (
          'Get started by creating your first assessment. You can add questions, set timers, and customize settings.'
        )}
      </p>
      {hasSearch && (
        <button className="clear-search-button" onClick={onClearSearch}>
          Clear Search
        </button>
      )}
    </div>
  );
};

const Assessments = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [jumpToPage, setJumpToPage] = useState('');

  const [loading, setLoading] = useState(true); 
  const [searching, setSearching] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  
  const abortControllerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Debounce search query
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setSearching(false);
      setPageNumber(1); // Reset to first page on new search
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const totalPages = Math.ceil(count / pageSize);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // Cycle through: asc -> desc -> none
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prev.direction === 'desc') {
          return { key: '', direction: '' };
        }
      }
      return { key, direction: 'asc' };
    });
    setPageNumber(1); // Reset to first page on sort
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg className="sort-icon sort-icon-default" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M6 2L8 5H4L6 2Z" fill="currentColor" opacity="0.3"/>
          <path d="M6 10L4 7H8L6 10Z" fill="currentColor" opacity="0.3"/>
        </svg>
      );
    }
    if (sortConfig.direction === 'asc') {
      return (
        <svg className="sort-icon sort-icon-asc" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M6 2L8 5H4L6 2Z" fill="#1e40af"/>
        </svg>
      );
    }
    return (
      <svg className="sort-icon sort-icon-desc" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M6 10L4 7H8L6 10Z" fill="#1e40af"/>
      </svg>
    );
  };

  useEffect(() => {
    fetchAssessments();
  }, [pageNumber, debouncedSearchQuery, pageSize, sortConfig]); 

  const fetchAssessments = async () => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      
      // Build query params
      const params = { 
        page: pageNumber,
        page_size: pageSize
      };

      // Add search query if exists
      if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
        params.name = debouncedSearchQuery.trim();
      }

      // Add sorting if exists
      if (sortConfig.key && sortConfig.direction) {
        const sortField = sortConfig.key;
        const ordering = sortConfig.direction === 'desc' ? `-${sortField}` : sortField;
        params.ordering = ordering;
      }

      const response = await axiosInstance.get(`/assessments/tests/`, {
        params,
        signal: abortControllerRef.current.signal
      });
  
      if (response.data && response.data?.data?.results) {
        setData(response.data.data?.results);
        setCount(response.data.data?.pagination.count);
        setNextPage(response.data.data?.pagination.next);
        setPrevPage(response.data.data?.pagination.previous);
      } else {
        setData([]);
        setCount(0);
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        // Request was cancelled, ignore
        console.log('Request cancelled');
      } else {
        console.error('Error fetching data', error);
        setData([]);
        setCount(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadTestFile = async (testId, testName) => {
    try {
      setDownloading(testId); // Show loader for this test
  
      const response = await axiosInstance.post(
        '/utils/download-test-file/',
        { test_id: testId },
        { responseType: 'blob' } // Ensure binary data is received
      );
  
      // Create a Blob from the response with XLSX MIME type
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
  
      // Create a link element to trigger the download
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `test_${testName}.xlsx`; // Use .xlsx extension
      document.body.appendChild(link);
      link.click();
  
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error downloading test file', error);
    } finally {
      setDownloading(null); // Reset loader after download
    }
  };
  

  
  const handleEditClick = (test) => {
    setSelectedTest(test);
    setShowPopup(true);
  };

  const formatDateTime = (dateTime) => {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    };
    return new Date(dateTime).toLocaleString('en-US', options);
  };

  
  const handleButtonClick = (assessment) => {
    // Directly navigate to the question table with assessment ID
    navigate(`/questiontable`, { state: { questions: assessment } });
  };
  

  const handlePreviewClick = (assessment) => {
    // Navigate to the preview page with only the assessment ID
    navigate(`/preview-segment`, { state: { testData: assessment } });
  };
  
  const handleClearSearch = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setPageNumber(1);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPageNumber(1); // Reset to first page when changing page size
  };

  const handlePageJump = () => {
    const pageNum = parseInt(jumpToPage);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPageNumber(pageNum);
      setJumpToPage('');
    }
  };

  const handlePageJumpKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePageJump();
    }
  };

  const getDisplayRange = () => {
    if (count === 0) return { start: 0, end: 0 };
    const start = (pageNumber - 1) * pageSize + 1;
    const end = Math.min(pageNumber * pageSize, count);
    return { start, end };
  };

  const { start, end } = getDisplayRange();

  return (
    <div className="assessments-container">
      <div className='drop-search-assement'>
        <div className="search-wrapper">
          <span className="search-icon">
            {searching ? (
              <svg className="search-loading-spinner" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="6" stroke="#3b82f6" strokeWidth="2" strokeDasharray="10 5" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="7" cy="7" r="5" stroke="#64748b" strokeWidth="2"/>
                <line x1="11" y1="11" x2="15" y2="15" stroke="#64748b" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </span>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="search-clear-btn"
              onClick={handleClearSearch}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
          {searching && <span className="search-status-text">Searching...</span>}
        </div>

        <div className="page-size-selector">
          <label htmlFor="pageSize">Show:</label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={handlePageSizeChange}
            className="page-size-dropdown"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {!loading && count > 0 && (
        <div className="results-info">
          Showing <strong>{start}-{end}</strong> of <strong>{count}</strong> assessments
          {debouncedSearchQuery && (
            <span className="search-term-display">
              {' '}for "<strong>{debouncedSearchQuery}</strong>"
            </span>
          )}
        </div>
      )}

        <table className="assessments-table">
      <thead>
        <tr>
          <th 
            onClick={() => handleSort('name')}
            className={`sortable-header ${sortConfig.key === 'name' ? 'active-sort' : ''}`}
          >
            <span className="header-content">
              Name {getSortIcon('name')}
            </span>
          </th>
          <th 
            onClick={() => handleSort('start_date_time')}
            className={`sortable-header ${sortConfig.key === 'start_date_time' ? 'active-sort' : ''}`}
          >
            <span className="header-content">
              Start Time {getSortIcon('start_date_time')}
            </span>
          </th>
          <th 
            onClick={() => handleSort('end_date_time')}
            className={`sortable-header ${sortConfig.key === 'end_date_time' ? 'active-sort' : ''}`}
          >
            <span className="header-content">
              End Time {getSortIcon('end_date_time')}
            </span>
          </th>
          <th 
            onClick={() => handleSort('created_at')}
            className={`sortable-header ${sortConfig.key === 'created_at' ? 'active-sort' : ''}`}
          >
            <span className="header-content">
              Created {getSortIcon('created_at')}
            </span>
          </th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <>
            {[...Array(pageSize)].map((_, index) => (
              <tr key={index}>
                <td colSpan="5" style={{ padding: 0, border: 'none', background: 'transparent' }}>
                  <SkeletonRow />
                </td>
              </tr>
            ))}
          </>
        ) : data.length > 0 ? (
          data.map((assessment) => (
            <tr
              key={assessment.id}
              className={assessment.is_active ? '' : 'red-row'}
            >
              <td>{assessment.name}</td>
              <td>{formatDateTime(assessment.start_date_time)}</td>
              <td>{formatDateTime(assessment.end_date_time)}</td>
              <td>{formatDateTime(assessment.created_at)}</td>
              <td className="button-group-assess">
                <div className="button-grid">
                <button
  className={`assessments-button ${downloading === assessment.id ? 'btn-loading' : ''}`}
  onClick={() => downloadTestFile(assessment.id, assessment.name)}
  disabled={!assessment.test_file || downloading === assessment.id}
>
  <span>{downloading === assessment.id ? 'Downloading...' : assessment.test_file ? 'Download' : 'No File'}</span>
</button>


                  <button
                    className="assessments-button"
                    onClick={() => handleEditClick(assessment)}
                  >
                    Edit
                  </button>
                  <CloneButton  
                  test_id={assessment.id}
                  test_name = {assessment.name}
                  />
                 <GenerateLinkButton 
                  test_id={assessment.id}
                 />
                  <button
                    className="assessments-button"
                    onClick={() => handleButtonClick(assessment.id)}
                  >
                    Questions
                  </button>
                  <button
                   className="assessments-button"
                   onClick={() => handlePreviewClick(assessment.id)}
                  >
                    Preview
                  </button>
                </div>
              </td>
            </tr>
          ))
        ) : null}
      </tbody>
    </table>
    
    {!loading && data.length === 0 && (
      <EmptyState 
        searchQuery={debouncedSearchQuery} 
        onClearSearch={handleClearSearch}
      />
    )}

      <div className="assessments-pagination">
        <div className="pagination-controls">
          <button
            className="pagination-button"
            onClick={() => setPageNumber(1)}
            disabled={!prevPage || loading}
            title="First page"
          >
            «
          </button>
          <button
            className="pagination-button"
            onClick={() => setPageNumber(pageNumber - 1)}
            disabled={!prevPage || loading}
            title="Previous page"
          >
            ‹
          </button>
          
          <div className="pagination-info">
            <span className="pagination-page">Page {pageNumber} of {totalPages || 1}</span>
            <div className="page-jump-container">
              <input
                type="number"
                min="1"
                max={totalPages}
                value={jumpToPage}
                onChange={(e) => setJumpToPage(e.target.value)}
                onKeyPress={handlePageJumpKeyPress}
                placeholder="Jump"
                className="page-jump-input"
                disabled={loading || totalPages <= 1}
              />
              <button
                className="page-jump-button"
                onClick={handlePageJump}
                disabled={loading || !jumpToPage || totalPages <= 1}
              >
                Go
              </button>
            </div>
          </div>

          <button
            className="pagination-button"
            onClick={() => setPageNumber(pageNumber + 1)}
            disabled={!nextPage || loading}
            title="Next page"
          >
            ›
          </button>
          <button
            className="pagination-button"
            onClick={() => setPageNumber(totalPages)}
            disabled={!nextPage || loading}
            title="Last page"
          >
            »
          </button>
        </div>
      </div>

      {showPopup && (
        <AssessmentEditbrn 
          fetchAssessments={fetchAssessments}
          selectedTest={selectedTest}
          setShowPopup={setShowPopup}
        />
      )}
    </div>
  );
};

export default Assessments;
